import { PackagingNode } from '../data/model'

export interface UsedBatches {
  [key: string]: number
}

/**
 * Counts the used batches from an array of PackagingNode objects.
 *
 * @param nodes - An array of PackagingNode objects to process.
 * @returns An object mapping batch numbers to their total quantities used.
 */
export const countUsedBatches = (nodes?: PackagingNode[]): UsedBatches => {
  // If no nodes are provided, return an empty object
  if (!nodes) return {}

  // Initialize an object to hold the count of used batches
  const batchMap: UsedBatches = {}

  // Iterate over each node in the provided nodes array
  nodes.forEach(node => {
    // Check if the current node's data has a PackagingType of 'Single' and contains Batches
    if (
      node.data?.PackagingType === 'Single' && // Check PackagingType
      node.data && // Ensure node.data is defined
      node.data.Batches // Check if Batches exist
    ) {
      // Iterate through each batch in the current node's Batches
      node.data.Batches.forEach(batch => {
        // If the batch number already exists in the batchMap, increment its quantity
        if (batchMap[batch.Batchnumber]) {
          batchMap[batch.Batchnumber] += batch.Quantity // Increment existing quantity
        } else {
          // If the batch number does not exist, initialize it with the current quantity
          batchMap[batch.Batchnumber] = batch.Quantity // Set initial quantity
        }
      })
    }

    // Check if the current node has children
    if (node.children) {
      // Recursively count used batches in the child nodes
      const childBatches = countUsedBatches(node.children)
      // Merge the child batches into the batchMap
      for (const [batchnumber, quantity] of Object.entries(childBatches)) {
        // If the batch number already exists, increment its quantity
        if (batchMap[batchnumber]) {
          batchMap[batchnumber] += quantity // Increment existing quantity
        } else {
          // If the batch number does not exist, initialize it with the child's quantity
          batchMap[batchnumber] = quantity // Set initial quantity
        }
      }
    }
  })

  // Return the final mapping of batch numbers to their total quantities
  return batchMap
}
